'use client';

import { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import { type CitationClickHandler } from './legal-text-block';
import { createMarkdownComponents, type MarkdownComponents, type WebSourceClickHandler } from './markdown-components';
import { MermaidModal } from '@/components/ui/mermaid';
import { cn } from '@/lib/utils';

export type { CitationClickHandler, WebSourceClickHandler };

interface MarkdownRendererProps {
  children: string;
  className?: string;
  components?: Partial<MarkdownComponents>;
  containerClassName?: string;
  enableCopy?: boolean;
  onCitationClick?: CitationClickHandler;
  isStreaming?: boolean;
}

export function MarkdownRenderer({
  children,
  className,
  components: customComponents,
  containerClassName,
  enableCopy = true,
  onCitationClick,
  isStreaming = false,
}: MarkdownRendererProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalChart, setModalChart] = useState<string | null>(null);

  const handleMermaidClick = useCallback((chart: string) => {
    setModalChart(chart);
    setModalOpen(true);
  }, []);

  // Memoized so re-renders with the same options do not rebuild the renderers.
  const components = useMemo(
    () =>
      createMarkdownComponents({
        onCitationClick,
        enableCopy,
        isStreaming,
        handleMermaidClick,
        customComponents,
      }),
    [onCitationClick, enableCopy, handleMermaidClick, isStreaming, customComponents],
  );

  // Memoize the parsed tree so re-renders with the same document do not
  // re-parse the whole markdown; ReactMarkdown parsing is the dominant cost.
  const markdownElement = useMemo(
    () => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        className={cn('w-full max-w-none', className)}
        components={components}
        urlTransform={(url) => url}
      >
        {children}
      </ReactMarkdown>
    ),
    [children, components, className],
  );

  const content = containerClassName ? (
    <article className={containerClassName}>{markdownElement}</article>
  ) : (
    markdownElement
  );

  if (enableCopy && modalChart) {
    return (
      <>
        {content}
        <MermaidModal chart={modalChart} open={modalOpen} onOpenChange={setModalOpen} />
      </>
    );
  }

  return content;
}
